/**
 * Built-in Figma Data Extractors
 * 基于现有项目的内置提取器实现
 */

import type {
  ExtractorFn,
  GlobalVars,
  StyleTypes,
  TraversalContext,
  SimplifiedNode,
  FigmaNode,
  SimplifiedLayout,
  SimplifiedTextStyle,
  SimplifiedFill,
  SimplifiedStroke,
  SimplifiedEffects,
} from './types';
import { generateVarId, hasValue } from './node-walker';

/**
 * Helper function to find or create a global variable.
 */
function findOrCreateVar(globalVars: GlobalVars, value: StyleTypes, prefix: string): string {
  // Check if the same value already exists
  const [existingVarId] =
    Object.entries(globalVars.styles).find(
      ([_, existingValue]) => JSON.stringify(existingValue) === JSON.stringify(value),
    ) ?? [];

  if (existingVarId) {
    return existingVarId;
  }

  // Create a new variable if it doesn't exist
  const varId = generateVarId(prefix);
  globalVars.styles[varId] = value;
  return varId;
}

/**
 * Extracts layout-related properties from a node.
 */
export const layoutExtractor: ExtractorFn = (node, result, context) => {
  const layout = buildSimplifiedLayout(node, context.parent);
  if (Object.keys(layout).length > 1) {
    result.layout = findOrCreateVar(context.globalVars, layout, "layout");
  }
};

/**
 * Extracts text content and text styling from a node.
 */
export const textExtractor: ExtractorFn = (node, result, context) => {
  // Extract text content
  if (isTextNode(node)) {
    result.text = extractNodeText(node);
  }

  // Extract text style
  if (hasTextStyle(node)) {
    const textStyle = extractTextStyle(node);
    if (textStyle) {
      // Prefer Figma named style when available
      const styleName = getStyleName(node, context, ["text", "typography"]);
      if (styleName) {
        context.globalVars.styles[styleName] = textStyle;
        result.textStyle = styleName;
      } else {
        result.textStyle = findOrCreateVar(context.globalVars, textStyle, "textStyle");
      }
    }
  }
};

/**
 * Extracts visual appearance properties (fills, strokes, effects, opacity, border radius).
 */
export const visualsExtractor: ExtractorFn = (node, result, context) => {
  // Check if node has children to determine CSS properties
  const hasChildren = hasValue("children", node) && Array.isArray(node.children) && node.children.length > 0;

  // fills
  if (hasValue("fills", node) && Array.isArray(node.fills) && node.fills.length) {
    const fills = node.fills.map((fill) => parseFill(fill, hasChildren)).reverse();
    const styleName = getStyleName(node, context, ["fill", "fills"]);
    if (styleName) {
      context.globalVars.styles[styleName] = fills;
      result.fills = styleName;
    } else {
      result.fills = findOrCreateVar(context.globalVars, fills, "fill");
    }
  }

  // strokes
  const strokes = buildSimplifiedStrokes(node, hasChildren);
  if (strokes.colors.length) {
    const styleName = getStyleName(node, context, ["stroke", "strokes"]);
    if (styleName) {
      // Only colors are stylable; keep other stroke props on the node
      context.globalVars.styles[styleName] = strokes.colors;
      result.strokes = styleName;
      if (strokes.strokeWeight) result.strokeWeight = strokes.strokeWeight;
      if (strokes.strokeDashes) result.strokeDashes = strokes.strokeDashes;
      if (strokes.strokeWeights) result.strokeWeights = strokes.strokeWeights;
    } else {
      result.strokes = findOrCreateVar(context.globalVars, strokes, "stroke");
    }
  }

  // effects
  const effects = buildSimplifiedEffects(node);
  if (Object.keys(effects).length) {
    const styleName = getStyleName(node, context, ["effect", "effects"]);
    if (styleName) {
      // Effects styles store only the effect values
      context.globalVars.styles[styleName] = effects;
      result.effects = styleName;
    } else {
      result.effects = findOrCreateVar(context.globalVars, effects, "effect");
    }
  }

  // opacity
  if (hasValue("opacity", node) && typeof node.opacity === "number" && node.opacity !== 1) {
    result.opacity = node.opacity;
  }

  // border radius
  if (hasValue("cornerRadius", node) && typeof node.cornerRadius === "number") {
    result.borderRadius = `${node.cornerRadius}px`;
  }
  if (hasValue("rectangleCornerRadii", node) && Array.isArray(node.rectangleCornerRadii)) {
    result.borderRadius = `${node.rectangleCornerRadii[0]}px ${node.rectangleCornerRadii[1]}px ${node.rectangleCornerRadii[2]}px ${node.rectangleCornerRadii[3]}px`;
  }
};

/**
 * Extracts component-related properties from INSTANCE nodes.
 */
export const componentExtractor: ExtractorFn = (node, result, _context) => {
  if (node.type === "INSTANCE") {
    if (hasValue("componentId", node)) {
      result.componentId = node.componentId;
    }

    // Add specific properties for instances of components
    if (hasValue("componentProperties", node)) {
      result.componentProperties = Object.entries(node.componentProperties ?? {}).map(
        ([name, { value, type }]) => ({
          name,
          value: value?.toString() || '',
          type: type || 'TEXT',
        }),
      );
    }
  }
};

// Helper to fetch a Figma style name for specific style keys on a node
function getStyleName(
  node: FigmaNode,
  context: TraversalContext,
  keys: string[],
): string | undefined {
  if (!hasValue("styles", node)) return undefined;
  const styleMap = node.styles as Record<string, string>;
  for (const key of keys) {
    const styleId = styleMap[key];
    if (styleId) {
      const meta = context.globalVars.extraStyles?.[styleId];
      if (meta?.name) return meta.name;
    }
  }
  return undefined;
}

// -------------------- UTILITY FUNCTIONS --------------------

/**
 * Build simplified layout from Figma node
 */
function buildSimplifiedLayout(node: FigmaNode, parent?: FigmaNode): SimplifiedLayout {
  const layout: SimplifiedLayout = {};

  // Basic positioning
  if (node.absoluteBoundingBox) {
    const { width, height } = node.absoluteBoundingBox;
    layout.width = `${width}px`;
    layout.height = `${height}px`;
  }

  // Auto layout properties (simplified)
  if (node.type === 'FRAME' && (node as any).layoutMode) {
    const layoutMode = (node as any).layoutMode;
    layout.display = 'flex';
    layout.flexDirection = layoutMode === 'HORIZONTAL' ? 'row' : 'column';
    
    // Alignment
    if ((node as any).primaryAxisAlignItems) {
      layout.justifyContent = convertAlignment((node as any).primaryAxisAlignItems);
    }
    if ((node as any).counterAxisAlignItems) {
      layout.alignItems = convertAlignment((node as any).counterAxisAlignItems);
    }
    
    // Gap
    if ((node as any).itemSpacing) {
      layout.gap = `${(node as any).itemSpacing}px`;
    }
  }

  return layout;
}

/**
 * Extract text content from text node
 */
function extractNodeText(node: FigmaNode): string {
  return node.characters || '';
}

/**
 * Extract text style from node
 */
function extractTextStyle(node: FigmaNode): SimplifiedTextStyle | null {
  if (!node.style) return null;

  const style = node.style;
  return {
    fontFamily: style.fontFamily || 'Inter',
    fontSize: style.fontSize || 16,
    fontWeight: style.fontWeight || 400,
    lineHeight: style.lineHeightPx ? style.lineHeightPx / (style.fontSize || 16) : undefined,
    letterSpacing: style.letterSpacing,
    textAlign: style.textAlignHorizontal?.toLowerCase(),
    color: style.fills?.[0] ? convertColorToString(style.fills[0]) : undefined,
  };
}

/**
 * Parse fill to simplified format
 */
function parseFill(fill: any, hasChildren: boolean): SimplifiedFill {
  const result: SimplifiedFill = {
    type: fill.type,
  };

  if (fill.type === 'SOLID' && fill.color) {
    result.color = convertColorToString(fill);
  }

  if (fill.opacity !== undefined && fill.opacity !== 1) {
    result.opacity = fill.opacity;
  }

  return result;
}

/**
 * Build simplified strokes
 */
function buildSimplifiedStrokes(node: FigmaNode, hasChildren: boolean): SimplifiedStroke {
  const result: SimplifiedStroke = {
    colors: [],
  };

  if (node.strokes && Array.isArray(node.strokes)) {
    result.colors = node.strokes.map(stroke => parseFill(stroke, hasChildren));
  }

  if ((node as any).strokeWeight) {
    result.strokeWeight = `${(node as any).strokeWeight}px`;
  }

  return result;
}

/**
 * Build simplified effects
 */
function buildSimplifiedEffects(node: FigmaNode): SimplifiedEffects {
  const effects: SimplifiedEffects = {};

  if (node.effects && Array.isArray(node.effects)) {
    const shadows = node.effects
      .filter(effect => effect.type === 'DROP_SHADOW' || effect.type === 'INNER_SHADOW')
      .map(effect => convertEffectToBoxShadow(effect));
    
    if (shadows.length > 0) {
      effects.boxShadow = shadows.join(', ');
    }
  }

  return effects;
}

/**
 * Check if node is a text node
 */
function isTextNode(node: FigmaNode): boolean {
  return node.type === 'TEXT';
}

/**
 * Check if node has text style
 */
function hasTextStyle(node: FigmaNode): boolean {
  return node.type === 'TEXT' && !!node.style;
}

/**
 * Convert Figma color to CSS string
 */
function convertColorToString(fill: any): string {
  if (!fill.color) return 'transparent';
  
  const { r, g, b, a = 1 } = fill.color;
  const alpha = fill.opacity !== undefined ? fill.opacity * a : a;
  
  if (alpha === 1) {
    return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
  } else {
    return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha})`;
  }
}

/**
 * Convert Figma alignment to CSS
 */
function convertAlignment(alignment: string): string {
  const alignmentMap: Record<string, string> = {
    'MIN': 'flex-start',
    'CENTER': 'center',
    'MAX': 'flex-end',
    'SPACE_BETWEEN': 'space-between',
  };
  return alignmentMap[alignment] || 'flex-start';
}

/**
 * Convert Figma effect to CSS box-shadow
 */
function convertEffectToBoxShadow(effect: any): string {
  const { offset, radius, color } = effect;
  const colorStr = convertColorToString({ color, opacity: effect.color?.a });
  return `${offset?.x || 0}px ${offset?.y || 0}px ${radius || 0}px ${colorStr}`;
}

// -------------------- CONVENIENCE COMBINATIONS --------------------

/**
 * All extractors - replicates the current parseNode behavior.
 */
export const allExtractors = [layoutExtractor, textExtractor, visualsExtractor, componentExtractor];

/**
 * Layout and text only - useful for content analysis and layout planning.
 */
export const layoutAndText = [layoutExtractor, textExtractor];

/**
 * Text content only - useful for content audits and copy extraction.
 */
export const contentOnly = [textExtractor];

/**
 * Visuals only - useful for design system analysis and style extraction.
 */
export const visualsOnly = [visualsExtractor];

/**
 * Layout only - useful for structure analysis.
 */
export const layoutOnly = [layoutExtractor];

/**
 * Components only - useful for component analysis.
 */
export const componentsOnly = [componentExtractor];

// -------------------- AFTER CHILDREN HELPERS --------------------

/**
 * SVG-eligible node types that can be collapsed into containers
 */
export const SVG_ELIGIBLE_TYPES = ['RECTANGLE', 'ELLIPSE', 'POLYGON', 'STAR', 'VECTOR', 'LINE'];

/**
 * Collapse SVG containers - combines multiple vector children into single SVG nodes
 */
export function collapseSvgContainers(
  node: FigmaNode,
  result: SimplifiedNode,
  children: SimplifiedNode[],
): SimplifiedNode[] {
  // If all children are SVG-eligible, collapse them
  const allChildrenAreSvgEligible = children.every(child => 
    SVG_ELIGIBLE_TYPES.includes(child.type)
  );

  if (allChildrenAreSvgEligible && children.length > 0) {
    // Modify the parent to be an SVG container
    result.type = 'IMAGE-SVG';
    // Return empty children array to collapse them
    return [];
  }

  return children;
}